import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import * as api from '../api/client';
import type { Task } from '../types/project';

type AuthState =
  | { status: 'loading' }
  | {
      status: 'authenticated';
      userId: string;
      personId: string | null;
      name: string | null;
      email: string | null;
      notifications: Task[];
      notificationCount: number;
    }
  | { status: 'unauthenticated' };

const AuthContext = createContext<{
  auth: AuthState;
  login: (password: string, personId: string) => Promise<void>;
  logout: () => Promise<void>;
  refetch: () => Promise<void>;
  notifications: Task[];
  notificationCount: number;
  markNotificationsRead: () => void;
} | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [auth, setAuth] = useState<AuthState>({ status: 'loading' });

  const refetch = useCallback(async () => {
    try {
      const data = await api.me();
      let notifications: Task[] = [];
      try {
        const notifResp = await api.fetchNotifications();
        notifications = notifResp.notifications;
      } catch {
        notifications = [];
      }
      setAuth({
        status: 'authenticated',
        userId: data.userId,
        personId: data.personId,
        name: data.name,
        email: data.email,
        notifications,
        notificationCount: notifications.length,
      });
    } catch {
      setAuth({ status: 'unauthenticated' });
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const login = useCallback(
    async (password: string, personId: string) => {
      await api.login(password, personId);
      await refetch();
    },
    [refetch]
  );

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setAuth({ status: 'unauthenticated' });
    }
  }, []);

  const markNotificationsRead = useCallback(() => {
    setAuth((prev) =>
      prev.status === 'authenticated' ? { ...prev, notificationCount: 0 } : prev
    );
  }, []);

  const notifications = auth.status === 'authenticated' ? auth.notifications : [];
  const notificationCount = auth.status === 'authenticated' ? auth.notificationCount : 0;

  return (
    <AuthContext.Provider
      value={{ auth, login, logout, refetch, notifications, notificationCount, markNotificationsRead }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
